import {
  ArrowUpRight,
  Clock3,
  MoreHorizontal,
  RefreshCcw,
  ShieldCheck,
  SlidersHorizontal,
  Star,
} from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Card } from "randmarcomps";
import { ManufacturerCategory, useGetV4ManufacturerByRouteManufacturerIdAccountQuery } from "randmar-api-client";
import Path from "../../routes/Path";
import ButtonWithActions from "../util/ButtonWithActions";
import CategoryActions from "./CategoryActions";

interface IProps {
  categories: ManufacturerCategory[];
  applicationId: string;
}

type BadgeTone = "primary" | "success" | "warning" | "muted";

const badgeToneStyles: Record<BadgeTone, string> = {
  primary: "border-[var(--ops-border-strong)] bg-[var(--ops-accent-muted)] text-[var(--ops-accent)]",
  success: "border-emerald-200 bg-emerald-50 text-emerald-700",
  warning: "border-amber-200 bg-amber-50 text-amber-800",
  muted: "border-gray-200 bg-gray-50 text-slate-600",
};

type GPTypeKey = "A_GP" | "B_GP" | "C_GP" | "D_GP";

const gpTypeDescriptions: Record<GPTypeKey, string> = {
  A_GP: "Exclusive",
  B_GP: "E-Commerce",
  C_GP: "Commercial",
  D_GP: "Buying Groups",
};

const StatusBadge: React.FC<{ tone?: BadgeTone; icon?: React.ReactNode; children: React.ReactNode }> = ({ tone = "muted", icon, children }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold tracking-tight ${badgeToneStyles[tone]}`}>
    {icon}
    {children}
  </span>
);

const FALLBACK_IMAGE = "https://api.randmar.io/Images/favicon.png";

const CategoryMarginsGrid: React.FC<IProps> = ({ categories, applicationId }) => {
  const { data: manufacturer } = useGetV4ManufacturerByRouteManufacturerIdAccountQuery({ routeManufacturerId: applicationId });
  const sortedCategories = React.useMemo(
    () => categories.slice().sort((a, b) => ((a.CategoryName || "").localeCompare(b.CategoryName || ""))),
    [categories]
  );

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    event.currentTarget.onerror = null;
    event.currentTarget.src = FALLBACK_IMAGE;
  };

  return (
    <div className="grid gap-5 sm:grid-cols-2 2xl:grid-cols-3">
      {sortedCategories.map(category => {
        const categoryName = category.CategoryName || "Untitled category";
        const categoryCode = category.CategoryCode || "N/A";
        const categoryLink = Path.category(applicationId, category.CategoryCode || "");
        const imageSrc = Path.categoryImage(manufacturer?.PublicName || applicationId, category.CategoryCode || "");
        const hasCustomSettings = category.WeeksOfStocking !== null || category.MinimumOrder != null || category.LeadTime !== null;
        const partnerAutoUpdateEnabled = manufacturer?.AutoUpdate || false;
        const autoReorderRequested = Boolean(category.AutoReorder);
        const autoReorderActive = autoReorderRequested && partnerAutoUpdateEnabled;

        const autoReorderTone: BadgeTone = autoReorderActive || autoReorderRequested ? "success" : "muted";
        const autoReorderCopy = autoReorderActive
          ? "Auto reorder active"
          : autoReorderRequested
            ? "Auto reordering"
            : "Manual reorder";
        const autoReorderIcon = autoReorderActive
          ? <RefreshCcw className="h-3.5 w-3.5" />
          : autoReorderRequested
            ? <RefreshCcw className="h-3.5 w-3.5" />
            : <SlidersHorizontal className="h-3.5 w-3.5" />;

        const qualificationTone: BadgeTone = category.QualificationRequired ? "primary" : "muted";
        const qualificationCopy = category.QualificationRequired ? "Qualification required" : "Open to all";

        const metricValue = (value: number | string | null | undefined, suffix?: string) => {
          if (value === null || value === undefined || value === "") return "Default";
          return `${value}${suffix || ""}`;
        };

        const marginMetrics: Array<{ label: string; value: number | string | null | undefined; key?: GPTypeKey }> = [
          { key: "A_GP", label: "A GP", value: category.A_GP },
          { key: "B_GP", label: "B GP", value: category.B_GP },
          { key: "C_GP", label: "C GP", value: category.C_GP },
          { key: "D_GP", label: "D GP", value: category.D_GP },
          { label: "Cost GP", value: category.Cost_GP },
        ];

        return (
          <Card
            key={`category-card-${categoryCode}`}
            className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-all duration-200 hover:border-[var(--ops-accent)] hover:shadow-md focus-within:ring-2 focus-within:ring-[var(--ops-focus-ring)]"
          >
            <div className="flex flex-col gap-6 p-6">
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-4">
                  <div className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-xl border border-gray-100 bg-gray-50 shadow-inner">
                    <img
                      src={imageSrc}
                      alt={`Category tile for ${categoryName}`}
                      className="h-full w-full object-cover"
                      loading="lazy"
                      decoding="async"
                      onError={handleImageError}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <Link
                      to={categoryLink}
                      className="line-clamp-2 block text-lg font-semibold text-slate-900 transition-colors hover:text-[color:var(--ops-accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)]"
                      title={categoryName}
                    >
                      {categoryName}
                    </Link>
                    <p className="text-sm text-slate-500" title={category.Niche || "Niche not assigned"}>
                      {category.Niche || "Niche not assigned"}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {category.Starred && (
                      <StatusBadge tone="warning" icon={<Star className="h-3.5 w-3.5 fill-current" fill="currentColor" />}>
                        Priority
                      </StatusBadge>
                    )}
                    <ButtonWithActions
                      variant="ghost"
                      icon={<MoreHorizontal className="h-5 w-5" />}
                      size="small"
                      className="h-9 w-9 p-0"
                    >
                      <CategoryActions category={category} />
                    </ButtonWithActions>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge tone="muted">
                    Code: {categoryCode}
                  </StatusBadge>
                  <StatusBadge tone={autoReorderTone} icon={autoReorderIcon}>
                    {autoReorderCopy}
                  </StatusBadge>
                  <StatusBadge tone={qualificationTone} icon={<ShieldCheck className="h-3.5 w-3.5" />}>
                    {qualificationCopy}
                  </StatusBadge>
                </div>
              </div>

              <div className="grid gap-4 rounded-xl border border-dashed border-gray-200 bg-[var(--ops-surface)] p-4 text-sm sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                {marginMetrics.map(metric => {
                  const description = metric.key ? gpTypeDescriptions[metric.key] : undefined;
                  return (
                    <div key={metric.label} className="space-y-1">
                      <div className="space-y-0.5 min-h-[2.25rem]">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          {metric.label}
                        </p>
                        {description && (
                          <p className="text-xs text-slate-500">{description}</p>
                        )}
                      </div>
                      <p className="text-base font-semibold text-slate-900">{metricValue(metric.value, "%")}</p>
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-dashed border-gray-200 pt-4 text-sm">
                <div className="flex items-center gap-2 text-slate-500">
                  {hasCustomSettings ? (
                    <>
                      <SlidersHorizontal className="h-4 w-4 text-[color:var(--ops-accent)]" aria-hidden="true" />
                      <span>Custom fulfillment rules</span>
                    </>
                  ) : (
                    <>
                      <Clock3 className="h-4 w-4 text-slate-400" aria-hidden="true" />
                      <span>Inherits manufacturer defaults</span>
                    </>
                  )}
                </div>
                <Link
                  to={categoryLink}
                  className="inline-flex items-center gap-1 font-semibold text-[color:var(--ops-accent)] transition-colors hover:text-[color:var(--ops-accent)]/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)]"
                >
                  Manage
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

export default CategoryMarginsGrid;
